import { ButtonV3 } from '@provi/provi-components'
import React, { useContext, useMemo } from 'react'
import phoneMockup from '../../../assets/images/phone-mockup.png'
import { MaxWidth } from '../../../components/max-width'
import { Template } from '../../../components/template'
import { TemplateContext } from '../../../components/template/context'
import { useInvoices } from './hooks'
import { InvoiceCoursing } from './invoice-coursing'
import {
  ButtonContainer,
  ButtonGroup,
  ButtonItemLeft,
  ButtonItemRight,
  OverdueBanner,
  OverdueHeader,
  OverdueIcon,
  OverdueTitle,
  OverdueText,
  OverdueCTAWrapper,
  ContainerImage,
  ContainerInvoices,
  ContainerNewPortal,
  ContainerNewPortalContainer,
  ContainerNewPortalText,
  ContainerNewPortalTitle,
  ContainerWarning,
  WarningText,
  Wrapper,
  SignatureBanner,
  SignatureBannerHeader,
  SignatureBannerHeaderIcon,
  SignatureBannerHeaderText,
  SignatureBannerDescription,
  SignaturesContainer,
  SignatureCard,
  SignatureCardHeader,
  SignatureCardTitle,
  SignatureStatusBadge,
  SignatureCardInfo,
  SignatureCardName,
  SignatureCardDetail,
  SignatureNote,
  SignatureNoteIcon,
  SignatureNoteText
} from './styles'
import { Br } from './invoice-coursing/styles'
import { history } from '../../../navigation/history'

const renderInvoiceCoursing = (item, getInvoice, renegotiation) => {
  const obj = {
    pending: <InvoiceCoursing.CardAvailable item={item} key={item.id} getInvoice={getInvoice} renegotiation={renegotiation} />,
    paid: <InvoiceCoursing.CardPaid item={item} key={item.id} getInvoice={getInvoice} />,
    delayed: <InvoiceCoursing.CardDelayed item={item} key={item.id} getInvoice={getInvoice} />,
    locked: <InvoiceCoursing.CardLocked item={item} key={item.id} />,
    delayed_blocked: <InvoiceCoursing.CardDelayedBlocked item={item} key={item.id} />
  }
  return obj[item.status]
}

const renderButtonGroup = (handleToggle, toggle) => {
  return (
    <ButtonGroup>
      <ButtonItemLeft onClick={() => handleToggle('open')} active={toggle === 'open'}>
        Em aberto
      </ButtonItemLeft>
      <ButtonItemRight onClick={() => handleToggle('paid')} active={toggle === 'paid'}>
        Pagas
      </ButtonItemRight>
    </ButtonGroup>
  )
}

export const Invoices = () => {
  const { setMenuActive, typeDash, setIsBack, setIsLoading, selectCrid, extraInfos } = useContext(TemplateContext)
  const isJudicializedCrid = extraInfos?.isJudicialized

  const [invoice, toggle, handleToggle, getInvoice, renegotiation] = useInvoices(
    setMenuActive,
    setIsBack,
    setIsLoading,
    selectCrid,
    typeDash
  )

  const getOptions = useMemo(() => {
    if (toggle === 'paid') {
      return invoice?.filter(item => item.status === 'paid')
    } else {
      return invoice?.filter(item => item.status !== 'paid')
    }
  }, [toggle, invoice])

  const options = getOptions
  const overdueCount = renegotiation?.delayedInstallments || 0
  const shouldShowBanner = renegotiation?.canRenegotiate && overdueCount > 0
  const overdueLabel = overdueCount === 1 ? 'parcela' : 'parcelas'

  // Verifica se existe signatureStatus com assinaturas pendentes
  const signatureStatus = renegotiation?.signatureStatus
  const recipients = signatureStatus?.recipients || []
  const studentRecipient = recipients.find(r => r.signer === 'User') || {}
  const guarantorRecipient = recipients.find(r => r.signer === 'Guarantor')
  const studentInfo = signatureStatus?.user
  const guarantorInfo = signatureStatus?.guarantor

  // Verifica se há alguma assinatura pendente (não assinada)
  const hasPendingSignature = recipients.some(r => r.signed === false)
  const hasSignatureData = signatureStatus && (recipients.length > 0 || studentInfo || guarantorInfo)
  const shouldShowSignatureBanner = hasSignatureData && hasPendingSignature

  // Helper para determinar status da assinatura
  const getSignatureStatus = recipient => {
    if (!recipient) return null
    const isSigned = recipient.signed === true
    return {
      variant: isSigned ? 'success' : 'pending',
      text: isSigned ? 'Assinado' : 'Pendente'
    }
  }

  const studentStatus = getSignatureStatus(studentRecipient)
  const guarantorStatus = guarantorRecipient ? getSignatureStatus(guarantorRecipient) : null
  const hasGuarantor = !!guarantorRecipient

  return (
    <Template>
      <MaxWidth>
        <Wrapper>
          {extraInfos?.isWithDifferentAgent ? (
            <ContainerNewPortal>
              <ContainerImage src={phoneMockup} />

              <ContainerNewPortalContainer>
                <ContainerNewPortalTitle>Seu jeito de pagar suas faturas mudou! ;)</ContainerNewPortalTitle>
                <ContainerNewPortalText>
                  Para ter acesso às suas faturas, acesse o nosso novo portal e realize o primeiro login:
                </ContainerNewPortalText>
                <ContainerNewPortalText>
                  <ul>
                    <li>
                      <strong>Insira seu CPF</strong> no campo indicado
                    </li>
                    <li>
                      A senha será <strong>somente os números da sua data de nascimento</strong>, sem barras ou traços
                    </li>
                    <li>Após o primeiro login, você poderá alterar sua senha</li>
                  </ul>
                </ContainerNewPortalText>
                <ButtonContainer>
                  <ButtonV3
                    text="Acessar novo portal"
                    buttonProps={{
                      href: 'https://principiapay.alunos.principia.net',
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }}
                  />
                </ButtonContainer>
              </ContainerNewPortalContainer>
            </ContainerNewPortal>
          ) : (
            renderButtonGroup(handleToggle, toggle)
          )}
          {shouldShowBanner && (
            <OverdueBanner>
              <OverdueHeader>
                <OverdueIcon>!</OverdueIcon>
                <OverdueTitle>{`Você possui ${overdueCount} ${overdueLabel} em atraso`}</OverdueTitle>
              </OverdueHeader>
              <OverdueText>
                Para regularizar, pague as parcelas vencidas ou renegocie o valor total do seu contrato.
              </OverdueText>
              <OverdueCTAWrapper>
                <ButtonV3 text="Renegociar contrato" onClick={() => history.push('/dashboard/negociacao/simulacao')} />
              </OverdueCTAWrapper>
            </OverdueBanner>
          )}
          {shouldShowSignatureBanner && (
            <SignatureBanner>
              <SignatureBannerHeader>
                <SignatureBannerHeaderIcon>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM11 10H15V12H9V5H11V10Z"
                      fill="white"
                      fillOpacity="0.92"
                    />
                  </svg>
                </SignatureBannerHeaderIcon>
                <SignatureBannerHeaderText>Aguardando assinaturas</SignatureBannerHeaderText>
              </SignatureBannerHeader>

              <SignatureBannerDescription>
                Para o acordo de renegociação ser validado, é necessário assinar o documento digitalmente.
              </SignatureBannerDescription>

              <SignaturesContainer>
                {studentInfo && (
                  <SignatureCard>
                    <SignatureCardHeader>
                      <SignatureCardTitle>Assinatura do aluno</SignatureCardTitle>
                      {studentStatus && (
                        <SignatureStatusBadge variant={studentStatus.variant}>{studentStatus.text}</SignatureStatusBadge>
                      )}
                    </SignatureCardHeader>
                    <SignatureCardInfo>
                      <SignatureCardName>{studentInfo.fullName || '-'}</SignatureCardName>
                      <SignatureCardDetail>CPF {studentInfo.CPF || '-'}</SignatureCardDetail>
                      <SignatureCardDetail>{studentInfo.email || '-'}</SignatureCardDetail>
                    </SignatureCardInfo>
                  </SignatureCard>
                )}
                {hasGuarantor && guarantorInfo && (
                  <SignatureCard>
                    <SignatureCardHeader>
                      <SignatureCardTitle>Assinatura do avalista</SignatureCardTitle>
                      {guarantorStatus && (
                        <SignatureStatusBadge variant={guarantorStatus.variant}>{guarantorStatus.text}</SignatureStatusBadge>
                      )}
                    </SignatureCardHeader>
                    <SignatureCardInfo>
                      <SignatureCardName>{guarantorInfo.fullName || '-'}</SignatureCardName>
                      <SignatureCardDetail>CPF {guarantorInfo.CPF || '-'}</SignatureCardDetail>
                      <SignatureCardDetail>{guarantorInfo.email || '-'}</SignatureCardDetail>
                    </SignatureCardInfo>
                  </SignatureCard>
                )}
              </SignaturesContainer>

              {hasGuarantor && (
                <SignatureNote>
                  <SignatureNoteIcon>
                    <svg width="18" height="18" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M10.4688 20C14.0234 20 17.3047 18.125 19.1016 15C20.8984 11.9141 20.8984 8.125 19.1016 5C17.3047 1.91406 14.0234 0 10.4688 0C6.875 0 3.59375 1.91406 1.79688 5C0 8.125 0 11.9141 1.79688 15C3.59375 18.125 6.875 20 10.4688 20ZM8.90625 13.125H9.84375V10.625H8.90625H7.96875V8.75H8.90625H10.7812H11.7188V9.6875V13.125H12.0312H12.9688V15H12.0312H8.90625H7.96875V13.125H8.90625ZM11.7188 7.5H9.21875V5H11.7188V7.5Z"
                        fill="#0088CC"
                      />
                    </svg>
                  </SignatureNoteIcon>
                  <SignatureNoteText>
                    O avalista receberá o link de assinatura exclusivamente no e-mail cadastrado. Após a conclusão da assinatura
                    digital, a fatura para pagamento da entrada será enviada automaticamente para o seu e-mail.
                  </SignatureNoteText>
                </SignatureNote>
              )}
            </SignatureBanner>
          )}
          {isJudicializedCrid && (
            <ContainerWarning>
              <WarningText size="large">
                Em procedimento de ajuizamento.
                <Br />
                Entrar em contato através do canal (11)93352-4203
              </WarningText>
            </ContainerWarning>
          )}
          {!shouldShowSignatureBanner && (
            <ContainerInvoices>{options.map(item => renderInvoiceCoursing(item, getInvoice, renegotiation))}</ContainerInvoices>
          )}
        </Wrapper>
      </MaxWidth>
    </Template>
  )
}
